import { ValueOf } from '@app/types/shared';
import { Design } from './design';
import { DesignAppProperty } from './design-app-property';
import { DesignCompoundIntent } from './design-compound-intent';
import { DesignCsvData } from './design-csv-data';
import { DesignFillerPrompt } from './design-filler-prompt';
import { DesignFolderModule } from './design-folder-module';
import { DesignFunction } from './design-function';
import { DesignGlobalIntent } from './design-global-intent';
import { DesignGraphQlSchema } from './design-graph-ql-schema';
import { DesignIntent } from './design-intent';
import { DesignModule } from './design-module';
import { DesignModuleLink } from './design-module-link';
import { DesignModuleLocalVariable } from './design-module-local-variable';
import { DesignModuleNode } from './design-module-node';
import { DesignObject } from './design-object';
import { DesignUnitTest } from './design-unit-test';
import { DesignVariable } from './design-variable';

type FlatCaches = {
  appProperties: DesignAppProperty;
  compoundIntents: DesignCompoundIntent;
  csvs: DesignCsvData;
  folderModules: DesignFolderModule;
  functions: DesignFunction;
  globalIntents: DesignGlobalIntent;
  graphQlSchemas: DesignGraphQlSchema;
  intents: DesignIntent;
  modules: DesignModule;
  objects: DesignObject;
  unitTests: DesignUnitTest;
  variables: DesignVariable;
}

type NestedCaches = {
  fillerPrompts: DesignFillerPrompt;
  moduleLinks: DesignModuleLink;
  moduleLocalVariables: DesignModuleLocalVariable;
  moduleNodes: DesignModuleNode;
};

type Caches = (
  {
    [K in keyof(FlatCaches)]: Map<string, FlatCaches[K]>;
  } & {
    [K in keyof(NestedCaches)]: Map<string, Map<string, NestedCaches[K]>>;
  }
);

/** Used for quick lookups of @see Design objects by their IDs. */
export class DesignMap implements Readonly<Caches> {
  private _caches: Partial<Caches> = {};
  private _design: Design;

  constructor(
    design: Design
  ) {
    this._design = design;
  }

  get appProperties(): Map<string, DesignAppProperty> {
    return this._getFlatCache('appProperties', this._design.appProperties);
  }

  get compoundIntents(): Map<string, DesignCompoundIntent> {
    return this._getFlatCache('compoundIntents', this._design.compoundIntents);
  }

  get csvs(): Map<string, DesignCsvData> {
    return this._getFlatCache('csvs', this._design.csvData);
  }

  get fillerPrompts(): Map<keyof(Design['fillerPrompts']), Map<string, DesignFillerPrompt>> {
    return this._getNestedCache(
      'fillerPrompts',
      Object.entries(this._design.fillerPrompts).map(([key, prompts]) => {
        return { id: key, prompts };
      }),
      (parent) => {
        return parent.prompts;
      }
    ) as Map<keyof(Design['fillerPrompts']), Map<string, DesignFillerPrompt>>;
  }

  get folderModules(): Map<string, DesignFolderModule> {
    return this._getFlatCache('folderModules', this._design.folders?.modules);
  }

  get functions(): Map<string, DesignFunction> {
    return this._getFlatCache('functions', this._design.functions);
  }

  get globalIntents(): Map<string, DesignGlobalIntent> {
    return this._getFlatCache('globalIntents', this._design.globalIntents);
  }

  get graphQlSchemas(): Map<string, DesignGraphQlSchema> {
    return this._getFlatCache('graphQlSchemas', this._design.graphQLSchemas);
  }

  get intents(): Map<string, DesignIntent> {
    return this._getFlatCache('intents', this._design.components.intents);
  }

  get modules(): Map<string, DesignModule> {
    return this._getFlatCache('modules', this._design.components.modules);
  }

  get moduleLinks(): Map<string, Map<string, DesignModuleLink>> {
    return this._getNestedCache(
      'moduleLinks',
      this._design.components.modules,
      (mod) => mod.versions[1].flow.links
    );
  }

  get moduleLocalVariables(): Map<string, Map<string, DesignModuleLocalVariable>> {
    return this._getNestedCache(
      'moduleLocalVariables',
      this._design.components.modules,
      (mod) => mod.localVariables
    );
  }

  get moduleNodes(): Map<string, Map<string, DesignModuleNode>> {
    return this._getNestedCache(
      'moduleNodes',
      this._design.components.modules,
      (mod) => mod.versions[1].flow.nodes
    );
  }

  get objects(): Map<string, DesignObject> {
    return this._getFlatCache('objects', this._design.components.objects);
  }

  get unitTests(): Map<string, DesignUnitTest> {
    return this._getFlatCache('unitTests', this._design.unitTests);
  }

  get variables(): Map<string, DesignVariable> {
    return this._getFlatCache('variables', this._design.components.variables);
  }

  private _createMap<K extends keyof(FlatCaches), T extends FlatCaches[K]>(
    key: K,
    items: T[] | undefined
  ): Map<string, T>;
  private _createMap<K extends keyof(NestedCaches), T extends NestedCaches[K]>(
    key: K,
    items: T[] | undefined
  ): Map<string, T>;
  private _createMap<
    K extends keyof(FlatCaches) | keyof(NestedCaches),
    T extends ValueOf<FlatCaches> | ValueOf<NestedCaches>
  >(
    key: K,
    items: T[] | undefined
  ): Map<string, T> {
    const backupIdKey = (() => {
      switch (key) {
        case 'appProperties':
          return 'config';
        case 'fillerPrompts':
          return 'name';
      }
    })();

    return new Map(items?.flatMap((item) => {
      const entries: [string, T][] = [];
      if ('id' in item && typeof item.id === 'string') {
        entries.push([item.id, item]);
      }

      if (
        backupIdKey
        && backupIdKey in item
        && typeof item[backupIdKey as keyof(T)] === 'string'
      ) {
        entries.push([item[backupIdKey as keyof(T)] as string, item]);
      }

      if (!entries.length) {
        const message = `Missing ID on '${key}' item.`;
        console.error(message, item);
        throw new Error(message);
      }

      return entries;
    }));
  }

  /** Create a map for an array of items on a @see Design. */
  private _getFlatCache<
    K extends keyof(FlatCaches),
    T extends FlatCaches[K],
  >(
    key: K,
    items: T[] | undefined
  ): Map<string, T> {
    return (this._caches[key] as Map<string, T> | null) ??= this._createMap<K, T>(key, items);
  }

  /** Create a map of maps for items with parents on a @see Design. */
  private _getNestedCache<
    K extends keyof(NestedCaches),
    TParent extends { id: string; },
    T extends NestedCaches[K]
  >(
    key: K,
    parents: TParent[],
    items: (parent: TParent) => T[] | undefined
  ): Map<string, Map<string, T>> {
    return (this._caches[key] as Map<string, Map<string, T>>) ??= new Map(
      parents.map((parent) => {
        return [parent.id, this._createMap(key, items(parent))];
      })
    );
  }
}
